import axios from "axios";
import { useEffect, useState } from "react";

interface DayForecast {
  temp: number;
  icon: string;
  tempmax: number;
  tempmin: number;
  humidity: number;
  windspeed: number;
  precipprob: number;
  description: string;
  datetimeEpoch: number;
}

interface CurrentConditions {
  temp: number;
  icon: string;
  humidity: number;
  windspeed: number;
  precipprob: number;
  datetimeEpoch: number;
}

interface WeatherForecast {
  address: string;
  latitude: number;
  timezone: string;
  tzoffset: number;
  queryCost: number;
  longitude: number;
  days: DayForecast[];
  resolvedAddress: string;
  currentConditions: CurrentConditions;
}

export interface ForecastDay {
  dayFull: string;
  dayShort: string;
  icon: string;
  tempmax: number;
  tempmin: number;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Busca a previsão do tempo (mesma fonte/lógica que o componente
 * Wheather já usava) e devolve a condição atual + os próximos 5 dias
 * já formatados em pt-BR, prontos pra exibir. Só busca quando
 * `enabled` é true (painel com clima ativado).
 */
export default function useWeather(enabled: boolean) {
  const [current, setCurrent] = useState<CurrentConditions | null>(null);
  const [forecast, setForecast] = useState<ForecastDay[]>([]);

  useEffect(() => {
    if (!enabled) return;

    const getInformationWheather = async () => {
      try {
        const { data } = await axios.get<WeatherForecast>(
          "https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/-23.4400582,-46.5734524?key=57QDSPYGLJ9B62YXCAG4SA64C&lang=pt&unitGroup=metric&iconSet=icons2&include=days,current&elements=datetimeEpoch,tempmax,tempmin,temp,humidity,windspeed,icon,precipprob,description"
        );

        setCurrent({
          ...data.currentConditions,
          precipprob: data.days[0]?.precipprob ?? data.currentConditions.precipprob,
        });

        const days: ForecastDay[] = [];
        data.days.forEach((item, index) => {
          if (index !== 0 && index <= 5) {
            const date = new Date(item.datetimeEpoch * 1000);
            const formatador = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });
            const nomeDoDia = formatador.format(date);
            const dayFull = capitalize(
              nomeDoDia.includes("-") ? nomeDoDia.split("-")[0] : nomeDoDia
            );
            days.push({
              dayFull,
              dayShort: dayFull.slice(0, 3),
              icon: item.icon,
              tempmax: Math.round(item.tempmax),
              tempmin: Math.round(item.tempmin),
            });
          }
        });
        setForecast(days);
      } catch (err) {
        // Mantém o cabeçalho sem clima em caso de falha na busca.
      }
    };

    getInformationWheather();
    const intervalId = setInterval(getInformationWheather, 3600000);

    return () => clearInterval(intervalId);
  }, [enabled]);

  return { current, forecast };
}
